/**
 * API 유틸리티 함수
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * 작업 상태 저장
 */
export async function saveJobStatus(jobId, userId, 상권, totalFiles, completedFiles, status = 'in_progress') {
  try {
    const response = await fetch(`${API_BASE_URL}/api/pdf/job-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jobId,
        userId,
        상권,
        totalFiles,
        completedFiles,
        status
      })
    });
    
    if (!response.ok) {
      throw new Error('작업 상태 저장 실패');
    }
    
    return await response.json();
  } catch (error) {
    console.error('작업 상태 저장 오류:', error);
    // 실패해도 진행은 계속되도록 에러를 무시
    return null;
  }
}

/**
 * 작업 ID로 작업 상태 조회 (진행률 확인용)
 */
export async function getJobStatus(jobId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/pdf/job-status/${jobId}`);
    
    if (!response.ok) {
      throw new Error('작업 상태 조회 실패');
    }
    
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('작업 상태 조회 오류:', error);
    return null;
  }
}

/**
 * 미완료 작업 조회
 */
export async function getIncompleteJob(userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/pdf/job-status/user/${userId}`);
    
    if (!response.ok) {
      throw new Error('작업 상태 조회 실패');
    }
    
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('작업 상태 조회 오류:', error);
    return null;
  }
}

/**
 * 작업 완료 처리
 */
export async function completeJob(jobId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/pdf/job-status/${jobId}/complete`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('작업 완료 처리 실패');
    }
    
    return await response.json();
  } catch (error) {
    console.error('작업 완료 처리 오류:', error);
    return null;
  }
}

/**
 * 백업 목록 조회
 */
export async function getBackups() {
  try {
    const { auth } = await import('../config/firebase');
    const userId = auth.currentUser?.uid || '';
    
    if (!userId) {
      throw new Error('로그인이 필요합니다.');
    }
    
    const response = await fetch(`${API_BASE_URL}/api/pdf/backups`, {
      headers: {
        'user-id': userId
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || '백업 목록 조회 실패');
    }
    
    const result = await response.json();
    // 서버에서 보낸 전체 응답 반환 (message, hint 포함)
    return result;
  } catch (error) {
    console.error('백업 목록 조회 오류:', error);
    throw error; // 에러를 다시 던져서 프론트엔드에서 처리할 수 있도록
  }
}

/**
 * 백업 복원
 */
export async function restoreBackup(jobId, selected상권s) {
  try {
    const { auth } = await import('../config/firebase');
    const userId = auth.currentUser?.uid || '';
    
    const response = await fetch(`${API_BASE_URL}/api/pdf/backups/${jobId}/restore`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'user-id': userId
      },
      body: JSON.stringify({ selected상권s })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || '백업 복원 실패');
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('백업 복원 오류:', error);
    throw error;
  }
}

/**
 * 삭제 예정 매물 정리 (로그인 시 호출)
 */
export async function cleanupScheduledDeletions() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/pdf/cleanup-scheduled-deletions`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('삭제 예정 매물 정리 실패');
    }
    
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('삭제 예정 매물 정리 오류:', error);
    return null;
  }
}

/**
 * 백엔드 서버 연결 상태 확인
 */
export async function checkBackendHealth() {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000) // 5초 타임아웃
    });
    return response.ok;
  } catch (error) {
    console.error('백엔드 헬스 체크 실패:', error);
    return false;
  }
}

/**
 * 역할 정보를 헤더에 추가하는 헬퍼 함수
 * @param {Object} headers - 기존 헤더 객체
 * @param {string} userRole - 사용자 역할
 * @returns {Object} 역할 정보가 추가된 헤더 객체
 */
function addRoleHeader(headers = {}, userRole = null) {
  if (userRole) {
    return {
      ...headers,
      'user-role': userRole
    };
  }
  return headers;
}

/**
 * 모든 매물 조회
 * @param {string} userRole - 사용자 역할 (선택사항, 필터링용)
 */
export async function fetchProperties(userRole = null, hasCoordinates = false) {
  try {
    const headers = addRoleHeader({}, userRole);
    const url = `${API_BASE_URL}/api/properties${hasCoordinates ? '?hasCoordinates=true' : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '매물 데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('매물 조회 오류:', error);
    throw error;
  }
}

/**
 * 특정 매물 조회
 * @param {string} id - 매물 ID
 * @param {string} userRole - 사용자 역할 (선택사항, 필터링용)
 */
export async function fetchPropertyById(id, userRole = null) {
  try {
    const headers = addRoleHeader({}, userRole);
    const response = await fetch(`${API_BASE_URL}/api/properties/${id}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '매물 데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('매물 조회 오류:', error);
    throw error;
  }
}

/**
 * 매물 생성
 * @param {Object} data - 매물 데이터
 * @param {string} userRole - 사용자 역할 (선택사항, DEMO면 더미 데이터로 생성)
 */
export async function createProperty(data, userRole = null) {
  try {
    const headers = addRoleHeader({
      'Content-Type': 'application/json',
    }, userRole);
    
    const response = await fetch(`${API_BASE_URL}/api/properties`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '매물 생성에 실패했습니다.');
    }
  } catch (error) {
    console.error('매물 생성 오류:', error);
    throw error;
  }
}

/**
 * 매물 일괄 생성
 * @param {Array} propertiesArray - 매물 배열
 * @param {string} userRole - 사용자 역할 (선택사항, DEMO면 더미 데이터로 생성)
 */
export async function createPropertiesBatch(propertiesArray, userRole = null) {
  try {
    const results = await Promise.allSettled(
      propertiesArray.map(property => createProperty(property, userRole))
    );
    
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    return {
      total: propertiesArray.length,
      successful,
      failed,
      results
    };
  } catch (error) {
    console.error('매물 일괄 생성 오류:', error);
    throw error;
  }
}

/**
 * 매물 삭제
 */
export async function deleteProperty(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/properties/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return true;
    } else {
      throw new Error(result.message || '매물 삭제에 실패했습니다.');
    }
  } catch (error) {
    console.error('매물 삭제 오류:', error);
    throw error;
  }
}

/**
 * 모든 매물 삭제
 */
export async function deleteAllProperties() {
  try {
    const properties = await fetchProperties();
    const results = await Promise.allSettled(
      properties.map(property => deleteProperty(property.id))
    );
    
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    return {
      total: properties.length,
      successful,
      failed
    };
  } catch (error) {
    console.error('매물 일괄 삭제 오류:', error);
    throw error;
  }
}

/**
 * 매물 업데이트
 */
export async function updateProperty(id, data) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/properties/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '매물 업데이트에 실패했습니다.');
    }
  } catch (error) {
    console.error('매물 업데이트 오류:', error);
    throw error;
  }
}

/**
 * 모든 상담 조회
 * @param {string} userRole - 사용자 역할 (선택사항, 필터링용)
 */
export async function fetchConsultations(userRole = null) {
  try {
    const headers = addRoleHeader({}, userRole);
    const response = await fetch(`${API_BASE_URL}/api/consultations`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '상담 데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('상담 조회 오류:', error);
    throw error;
  }
}

/**
 * 특정 상담 조회
 * @param {string} id - 상담 ID
 * @param {string} userRole - 사용자 역할 (선택사항, 필터링용)
 */
export async function fetchConsultationById(id, userRole = null) {
  try {
    const headers = addRoleHeader({}, userRole);
    const response = await fetch(`${API_BASE_URL}/api/consultations/${id}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '상담 데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('상담 조회 오류:', error);
    throw error;
  }
}

/**
 * 상담 생성
 * @param {Object} data - 상담 데이터
 * @param {string} userRole - 사용자 역할 (선택사항, DEMO면 더미 데이터로 생성)
 */
export async function createConsultation(data, userRole = null) {
  try {
    const headers = addRoleHeader({
      'Content-Type': 'application/json',
    }, userRole);
    
    const response = await fetch(`${API_BASE_URL}/api/consultations`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '상담 생성에 실패했습니다.');
    }
  } catch (error) {
    console.error('상담 생성 오류:', error);
    throw error;
  }
}

/**
 * 상담 업데이트
 */
export async function updateConsultation(id, data) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/consultations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '상담 업데이트에 실패했습니다.');
    }
  } catch (error) {
    console.error('상담 업데이트 오류:', error);
    throw error;
  }
}

/**
 * 상담 삭제
 */
export async function deleteConsultation(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/consultations/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return true;
    } else {
      throw new Error(result.message || '상담 삭제에 실패했습니다.');
    }
  } catch (error) {
    console.error('상담 삭제 오류:', error);
    throw error;
  }
}

/**
 * 여러 PDF 파일 분석 (변경 사항 확인, 실제 작업은 수행하지 않음)
 * 클라우드 런 요청 크기 제한(32MB)을 고려하여 배치 처리
 */
export async function analyzeMultiplePdfs(files, 상권, userId = null, onProgress = null) {
  try {
    const formData = new FormData();
    
    // 파일 추가
    let totalSize = 0;
    files.forEach((file, index) => {
      formData.append('pdfs', file);
      formData.append(`fileName_${index}`, file.name);
      totalSize += file.size;
    });
    
    formData.append('fileCount', files.length.toString());
    formData.append('상권', 상권);
    formData.append('sangkwon', 상권);
    
    console.log(`=== PDF 분석 요청 시작 ===`);
    console.log(`파일 수: ${files.length}개`);
    console.log(`총 파일 크기: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
    console.log(`상권: ${상권}`);
    console.log(`API URL: ${API_BASE_URL}`);
    console.log(`엔드포인트: ${API_BASE_URL}/api/pdf/analyze-multiple`);
    
      // 클라우드 런 요청 크기 제한(32MB) 고려하여 배치 처리
      // 사용자는 한번에 업로드하지만, 내부적으로 32MB를 넘으면 자동으로 배치로 나눠서 처리
      // FormData 오버헤드와 다른 필드들을 고려하여 25MB로 제한
      const maxBatchSize = 25 * 1024 * 1024; // 25MB (Cloud Run 32MB 제한 고려, 여유 공간 확보)
      const batches = [];
      let currentBatch = [];
      let currentBatchSize = 0;
      
      files.forEach((file, index) => {
        // 새 배치가 필요한 경우
        if (currentBatchSize + file.size > maxBatchSize && currentBatch.length > 0) {
          batches.push(currentBatch);
          currentBatch = [];
          currentBatchSize = 0;
        }
        currentBatch.push(file);
        currentBatchSize += file.size;
      });
      
      // 마지막 배치 추가
      if (currentBatch.length > 0) {
        batches.push(currentBatch);
      }
      
      console.log(`총 ${files.length}개 파일을 ${batches.length}개 배치로 나눔 (배치당 최대 ${(maxBatchSize / 1024 / 1024).toFixed(0)}MB, 순차 처리)`);
      
      // 작업 ID 생성 (진행 상황 추적용)
      const jobId = userId ? `pdf_${Date.now()}_${Math.random().toString(36).substring(2, 11)}` : null;
      
      // 작업 시작 시 상태 저장
      if (jobId && userId) {
        await saveJobStatus(jobId, userId, 상권, files.length, 0, 'in_progress');
      }
      
      // 배치가 1개여도 크기를 확인하고, 32MB를 초과하면 배치로 나누기
      const singleBatchSize = batches.length > 0 ? batches[0].reduce((sum, file) => sum + file.size, 0) : 0;
      if (batches.length === 1 && singleBatchSize <= maxBatchSize) {
        const startTime = Date.now();
        // 타임아웃: 최소 10분, 최대 2시간 (7200초), 파일당 10초 계산 (Cloud Run 3600초 제한 고려)
        // Cloud Run 최대 타임아웃이 3600초이므로, 프론트엔드는 그보다 약간 길게 설정
        const timeoutMs = Math.max(600000, Math.min(7200000, files.length * 10000));
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error(`요청 타임아웃: ${timeoutMs}ms (${timeoutMs / 1000}초) 경과`);
          controller.abort();
        }, timeoutMs);
        
        console.log(`요청 시작: ${API_BASE_URL}/api/pdf/analyze-multiple`);
        console.log(`타임아웃 설정: ${timeoutMs}ms (${timeoutMs / 1000}초)`);
        console.log(`파일 수: ${files.length}개, 총 크기: ${(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)}MB`);
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/pdf/analyze-multiple`, {
            method: 'POST',
            body: formData,
            signal: controller.signal,
            // 타임아웃을 더 길게 설정하기 위해 keepalive 사용
            keepalive: false
          });
          clearTimeout(timeoutId);
          const endTime = Date.now();
          console.log(`✅ 요청 완료: ${endTime - startTime}ms (${(endTime - startTime) / 1000}초), 상태 코드: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || `HTTP 오류! 상태 코드: ${response.status}`;
      } catch {
        errorMessage = `HTTP 오류! 상태 코드: ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    
          if (result.success) {
            // 단일 배치 완료 시 작업 완료 처리
            if (jobId && userId) {
              await saveJobStatus(jobId, userId, 상권, files.length, files.length, 'completed');
            }
            return result.data;
          } else {
            // 실패 시 작업 상태 업데이트
            if (jobId && userId) {
              await saveJobStatus(jobId, userId, 상권, files.length, 0, 'failed');
            }
            throw new Error(result.message || 'PDF 분석에 실패했습니다.');
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error(
              `요청 시간이 초과되었습니다. (${files.length}개 파일 처리 중)\n` +
              `파일이 너무 많거나 크기가 커서 처리 시간이 오래 걸립니다.\n` +
              `파일을 더 작은 그룹으로 나눠서 업로드해주세요.`
            );
          }
          throw fetchError;
        }
      } else {
        // 여러 배치로 나눠서 처리
        console.log(`배치 처리 시작: ${batches.length}개 배치`);
        const allResults = {
          new: [],
          updated: [],
          skipped: [],
          completed: [],
          total: 0,
          errors: [],
          _analysisData: {
            pdfDataList: [],
            상권: 상권,
            fileTo상권Map: {}
          }
        };
        
        // 모든 배치의 파일명과 키를 수집하기 위한 Set
        const allUploadedFileNames = new Set();
        const allUploadedKeys = new Set();
        let completedFilesCount = 0;
        
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          console.log(`배치 ${i + 1}/${batches.length} 처리 중: ${batch.length}개 파일`);
          
          const batchFormData = new FormData();
          batch.forEach((file, index) => {
            batchFormData.append('pdfs', file);
            batchFormData.append(`fileName_${index}`, file.name);
          });
          batchFormData.append('fileCount', batch.length.toString());
          batchFormData.append('상권', 상권);
          batchFormData.append('sangkwon', 상권);
          
          // 타임아웃: 최소 1분, 최대 1시간 (3600초), 파일당 2초 계산
          const timeoutMs = Math.max(60000, Math.min(3600000, batch.length * 2000));
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/pdf/analyze-multiple`, {
              method: 'POST',
              body: batchFormData,
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              const errorText = await response.text();
              let errorMessage;
              try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.message || `HTTP 오류! 상태 코드: ${response.status}`;
              } catch {
                errorMessage = `HTTP 오류! 상태 코드: ${response.status}`;
              }
              throw new Error(`배치 ${i + 1} 처리 실패: ${errorMessage}`);
            }
            
            const result = await response.json();
            
            if (result.success && result.data) {
              // 배치 완료 - 진행 상황 업데이트
              completedFilesCount += batch.length;
              if (onProgress) {
                onProgress(completedFilesCount, files.length, `배치 ${i + 1}/${batches.length} 완료`);
              }
              if (jobId && userId) {
                await saveJobStatus(jobId, userId, 상권, files.length, completedFilesCount, 'in_progress');
              }
              
              // 결과 병합 (completed는 제외 - 나중에 전체 파일 목록으로 재계산)
              allResults.new.push(...(result.data.new || []));
              allResults.updated.push(...(result.data.updated || []));
              allResults.skipped.push(...(result.data.skipped || []));
              // completed는 나중에 전체 파일 목록으로 재계산하므로 여기서는 수집만
              allResults.total += result.data.total || 0;
              if (result.data.errors) {
                allResults.errors.push(...result.data.errors);
              }
              if (result.data._analysisData && result.data._analysisData.pdfDataList) {
                allResults._analysisData.pdfDataList.push(...result.data._analysisData.pdfDataList);
              }
              
              // 모든 배치의 파일명과 키 수집
              if (result.data.new) {
                result.data.new.forEach(item => {
                  allUploadedFileNames.add(item.fileName);
                  if (item.상호) {
                    allUploadedKeys.add(`${item.fileName}|${item.상호}`);
                  }
                });
              }
              if (result.data.updated) {
                result.data.updated.forEach(item => {
                  allUploadedFileNames.add(item.fileName);
                  if (item.상호) {
                    allUploadedKeys.add(`${item.fileName}|${item.상호}`);
                  }
                });
              }
              if (result.data.skipped) {
                result.data.skipped.forEach(item => {
                  allUploadedFileNames.add(item.fileName);
                  if (item.상호) {
                    allUploadedKeys.add(`${item.fileName}|${item.상호}`);
                  }
                });
              }
            } else {
              throw new Error(result.message || `배치 ${i + 1} 분석 실패`);
            }
          } catch (batchError) {
            console.error(`배치 ${i + 1} 오류:`, batchError);
            if (batchError.name === 'AbortError') {
              throw new Error(`배치 ${i + 1} 처리 시간 초과`);
            }
            throw batchError;
          }
        }
        
        // 모든 배치 처리 완료 후, 전체 파일 목록으로 삭제 대상 재계산
        console.log(`모든 배치 처리 완료: 총 ${allResults.total}개 파일`);
        console.log(`전체 업로드된 파일명: ${allUploadedFileNames.size}개, 키: ${allUploadedKeys.size}개`);
        console.log(`삭제 대상 재계산 요청: 상권 "${상권}"`);
        
        // 진행 상황 업데이트 (재계산 중)
        if (onProgress) {
          onProgress(files.length, files.length, '삭제 대상 재계산 중');
        }
        
        // 작업 완료 처리
        if (jobId && userId) {
          await saveJobStatus(jobId, userId, 상권, files.length, files.length, 'completed');
        }
        
        try {
          // 백엔드에 전체 파일 목록을 전달하여 삭제 대상 재계산 요청
          const recalcResponse = await fetch(`${API_BASE_URL}/api/pdf/recalculate-completed`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uploadedFileNames: Array.from(allUploadedFileNames),
              uploadedKeys: Array.from(allUploadedKeys),
              상권: 상권,
              userRole: localStorage.getItem('userRole') || 'admin'
            })
          });
          
          if (recalcResponse.ok) {
            const recalcResult = await recalcResponse.json();
            if (recalcResult.success && recalcResult.data) {
              // 재계산된 삭제 대상으로 교체
              allResults.completed = recalcResult.data.completed || [];
              console.log(`삭제 대상 재계산 완료: ${allResults.completed.length}개`);
            } else {
              console.warn('삭제 대상 재계산 실패, 배치별 결과 사용:', recalcResult.message);
            }
          } else {
            console.warn('삭제 대상 재계산 요청 실패, 배치별 결과 사용');
          }
        } catch (recalcError) {
          console.warn('삭제 대상 재계산 중 오류 발생, 배치별 결과 사용:', recalcError);
        }
        
        // 최종 완료 진행 상황 업데이트
        if (onProgress) {
          onProgress(files.length, files.length, '완료');
        }
        
        return allResults;
      }
  } catch (error) {
    console.error('PDF 분석 오류:', error);
    console.error('에러 상세:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause
    });
    
    // 네트워크 오류인 경우 더 명확한 메시지 제공
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      const errorMsg = `백엔드 서버에 연결할 수 없습니다.\n` +
        `API URL: ${API_BASE_URL}\n\n` +
        `가능한 원인:\n` +
        `1. 네트워크 연결 문제\n` +
        `2. 요청 타임아웃 (파일이 너무 많거나 크기가 클 수 있습니다)\n` +
        `3. Cloud Run 타임아웃 (최대 1시간)\n\n` +
        `에러 상세: ${error.message}`;
      console.error('네트워크 오류 감지:', errorMsg);
      throw new Error(errorMsg);
    }
    
    // AbortError (타임아웃)인 경우
    if (error.name === 'AbortError') {
      const errorMsg = `요청이 타임아웃되었습니다.\n\n` +
        `파일이 너무 많거나 크기가 커서 처리 시간이 오래 걸립니다.\n` +
        `Cloud Run의 최대 타임아웃은 1시간(3600초)입니다.\n\n` +
        `해결 방법:\n` +
        `1. 파일을 더 작은 그룹으로 나눠서 업로드\n` +
        `2. Cloud Run 타임아웃을 최대값(3600초)으로 설정 확인`;
      console.error('타임아웃 오류:', errorMsg);
      throw new Error(errorMsg);
    }
    
    // 기존 에러 메시지가 있으면 그대로 사용
    if (error.message) {
      throw error;
    }
    
    throw new Error('PDF 분석 중 오류가 발생했습니다: ' + error.message);
  }
}

/**
 * 분석 결과를 바탕으로 실제 작업 수행 (승인 후 호출)
 */
export async function processMultiplePdfs(analysisData, 상권, customPrompt = null, userId = null, jobId = null) {
  try {
    console.log(`PDF 처리 요청 시작: API URL: ${API_BASE_URL}`);
    console.log(`엔드포인트: ${API_BASE_URL}/api/pdf/process-multiple`);
    
    // 작업 ID 생성 (진행률 추적용) - 전달받지 않았으면 생성
    const finalJobId = jobId || `job_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    // 요청 본문 생성 및 크기 확인
    const requestBody = {
      analysisData,
      상권,
      customPrompt,
      jobId: finalJobId
    };
    const requestBodyString = JSON.stringify(requestBody);
    const requestBodySize = new Blob([requestBodyString]).size;
    const requestBodySizeMB = (requestBodySize / 1024 / 1024).toFixed(2);
    
    console.log(`요청 본문 크기: ${requestBodySizeMB}MB (${requestBodySize} bytes)`);
    console.log(`분석 데이터 파일 수: ${analysisData?.pdfDataList?.length || 0}개`);
    
    // 요청 본문이 50MB를 초과하면 경고
    if (requestBodySize > 50 * 1024 * 1024) {
      console.warn(`⚠️ 요청 본문이 50MB를 초과합니다 (${requestBodySizeMB}MB). 백엔드 제한에 걸릴 수 있습니다.`);
    }
    
    // 타임아웃 설정 (대용량 처리 고려)
    const controller = new AbortController();
    const timeoutMs = 3600000; // 1시간
    const timeoutId = setTimeout(() => {
      console.error(`요청 타임아웃: ${timeoutMs}ms (${timeoutMs / 1000}초) 경과`);
      controller.abort();
    }, timeoutMs);
    
    try {
      console.log(`요청 전송 시작...`);
      const response = await fetch(`${API_BASE_URL}/api/pdf/process-multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId || '', // 진행률 추적용
        },
        body: requestBodyString,
        signal: controller.signal
      });
      console.log(`응답 수신: 상태 코드 ${response.status}`);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || `HTTP 오류! 상태 코드: ${response.status}`;
        } catch {
          errorMessage = `HTTP 오류! 상태 코드: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.success) {
        // jobId를 반환하여 진행률 조회 가능하도록 함
        return {
          ...result.data,
          jobId: result.data.jobId || finalJobId
        };
      } else {
        throw new Error(result.message || 'PDF 처리에 실패했습니다.');
      }
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error(`요청이 타임아웃되었습니다. (${timeoutMs / 1000}초) 파일이 너무 많거나 크기가 클 수 있습니다.`);
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('PDF 처리 오류:', error);
    console.error('에러 상세:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause
    });
    
    // 네트워크 오류인 경우 더 명확한 메시지 제공
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      const errorMsg = `백엔드 서버에 연결할 수 없습니다.\n` +
        `API URL: ${API_BASE_URL}\n\n` +
        `가능한 원인:\n` +
        `1. 네트워크 연결 문제\n` +
        `2. 요청 타임아웃 (파일이 너무 많거나 크기가 클 수 있습니다)\n` +
        `3. Cloud Run 타임아웃 (최대 1시간)\n\n` +
        `에러 상세: ${error.message}`;
      console.error('네트워크 오류 감지:', errorMsg);
      throw new Error(errorMsg);
    }
    
    // 기존 에러 메시지가 있으면 그대로 사용
    if (error.message) {
      throw error;
    }
    
    throw new Error('PDF 처리 중 오류가 발생했습니다: ' + error.message);
  }
}

/**
 * PDF 해시 정보 마이그레이션 (기존 매물에 해시 정보 추가)
 * @param {File[]} files - PDF 파일 배열
 * @param {Function} onProgress - 진행 상황 콜백 (current, total)
 * @param {string} userRole - 사용자 역할 (선택사항)
 */
export async function migrateHashInfo(files, onProgress = null, userRole = null) {
  try {
    // 마이그레이션도 한번에 처리 가능하도록 배치 크기 증가
    // Cloud Run 32MB 제한 고려하여 파일당 평균 200KB 가정 시 약 150개까지 가능
    const BATCH_SIZE = 100; // 배치 크기 증가 (한번에 더 많은 파일 처리)
    const batches = [];
    
    // 파일을 배치로 나누기
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      batches.push(files.slice(i, i + BATCH_SIZE));
    }
    
    const results = {
      success: 0,
      failed: 0,
      notFound: 0,
      skipped: 0, // 이미 마이그레이션된 매물 수
      details: []
    };
    
    let processedCount = 0;
    
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const formData = new FormData();
      
      batch.forEach((file, index) => {
        formData.append('pdfs', file);
        formData.append(`fileName_${index}`, file.name);
      });
      
      formData.append('fileCount', batch.length.toString());
      
      const headers = {};
      if (userRole) {
        headers['user-role'] = userRole;
      }
      
      // 타임아웃: 최소 1분, 최대 1시간 (3600초), 파일당 2초 계산
      const timeoutMs = Math.max(60000, Math.min(3600000, batch.length * 2000));
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/pdf/migrate-hash-info`, {
          method: 'POST',
          body: formData,
          headers,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || `HTTP 오류! 상태 코드: ${response.status}`;
          } catch {
            errorMessage = `HTTP 오류! 상태 코드: ${response.status}`;
          }
          throw new Error(`배치 ${batchIndex + 1} 처리 실패: ${errorMessage}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          results.success += result.data.success || 0;
          results.failed += result.data.failed || 0;
          results.notFound += result.data.notFound || 0;
          results.skipped += result.data.skipped || 0;
          if (result.data.details) {
            results.details.push(...result.data.details);
          }
        } else {
          throw new Error(result.message || `배치 ${batchIndex + 1} 마이그레이션 실패`);
        }
        
        processedCount += batch.length;
        if (onProgress) {
          onProgress(processedCount, files.length);
        }
        
        // 배치 간 짧은 대기 (서버 부하 방지)
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (batchError) {
        clearTimeout(timeoutId);
        console.error(`배치 ${batchIndex + 1} 오류:`, batchError);
        
        if (batchError.name === 'AbortError') {
          throw new Error(`배치 ${batchIndex + 1} 처리 시간 초과 (최대 1시간)`);
        }
        
        // 배치 실패 시 각 파일을 실패로 기록
        batch.forEach(file => {
          results.failed++;
          results.details.push({
            fileName: file.name,
            status: 'failed',
            message: batchError.message
          });
        });
        
        processedCount += batch.length;
        if (onProgress) {
          onProgress(processedCount, files.length);
        }
      }
    }
    
    return results;
  } catch (error) {
    console.error('마이그레이션 오류:', error);
    console.error('에러 상세:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause
    });
    
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      const errorMsg = `백엔드 서버에 연결할 수 없습니다.\n` +
        `API URL: ${API_BASE_URL}\n\n` +
        `가능한 원인:\n` +
        `1. 네트워크 연결 문제\n` +
        `2. 요청 타임아웃 (파일이 너무 많거나 크기가 클 수 있습니다)\n` +
        `3. Cloud Run 타임아웃 (최대 1시간)\n\n` +
        `에러 상세: ${error.message}`;
      console.error('네트워크 오류 감지:', errorMsg);
      throw new Error(errorMsg);
    }
    
    throw error;
  }
}

/**
 * 모든 공지사항 조회
 * @param {string} userRole - 사용자 역할 (선택사항)
 */
export async function fetchNotices(userRole = null) {
  try {
    const headers = addRoleHeader({}, userRole);
    const response = await fetch(`${API_BASE_URL}/api/notices`, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '공지사항 데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('공지사항 조회 오류:', error);
    throw error;
  }
}

/**
 * 최신 공지사항 조회
 * @param {string} userRole - 사용자 역할 (선택사항)
 */
export async function fetchLatestNotice(userRole = null) {
  try {
    const headers = addRoleHeader({}, userRole);
    const response = await fetch(`${API_BASE_URL}/api/notices/latest`, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data; // null일 수 있음
    } else {
      throw new Error(result.message || '최신 공지사항을 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('최신 공지사항 조회 오류:', error);
    throw error;
  }
}

/**
 * 특정 공지사항 조회
 * @param {string} id - 공지사항 ID
 * @param {string} userRole - 사용자 역할 (선택사항)
 */
export async function fetchNoticeById(id, userRole = null) {
  try {
    const headers = addRoleHeader({}, userRole);
    const response = await fetch(`${API_BASE_URL}/api/notices/${id}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || '공지사항 데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('공지사항 조회 오류:', error);
    throw error;
  }
}


